/**
 * Rehearsal — `/rehearsal` and `/rehearsal/:sessionId`
 *
 * Wires the anonymous-rehearsal end-to-end flow:
 *   1. POST `/v1/rehearsal/sessions` to create an ephemeral session
 *   2. Open a `StageSocketClient` against `/v1/rehearsal/<id>/stage`
 *   3. Render the three-column stage (score + cues + take log)
 *   4. Pipe inbound `server.note` events into the take log; let the
 *      "send cue" button push a `client.cue` back through the socket.
 *
 * Voice wiring (Track H Lane B):
 *   - `voiceMode` state tracks the current VoiceMode for this session.
 *   - `activeBanner` state holds the current FallbackBanner (or null).
 *   - A mic-toggle button is rendered when voiceMode !== 'unsupported'.
 *   - A voice-specific banner section is rendered when activeBanner is set.
 *   - The existing socket-error banner and retry logic are unchanged.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { newUlid, StageSocketClient } from "@/api";
import type { StageMessage, StageSocketState } from "@/api";
import type { CastMember } from "@/components/rehearsal/CastRail";
import type { Cue } from "@/components/primitives/CueRail";
import {
  applyPanelFrame,
  emptyPanelState,
  viewFor,
  type PanelState,
} from "@/rehearsal/panelState";
import { bannerReasonFor, type BannerReason } from "@/rehearsal/bannerReason";
import { treatmentFor } from "@/rehearsal/emptyReply";
import { conversationCopy } from "@/components/rehearsal/Conversation.copy";
import { createSession, type SessionCache } from "@/rehearsal/session";
import type { ScoreDraft } from "@/components/primitives/ScoreEditor";
import type { LogLine } from "@/components/primitives/TakeLogStream";
import { rehearsalStageCopy } from "@/components/rehearsal/RehearsalStage.copy";
import RehearsalStage from "@/components/rehearsal/RehearsalStage";
import Conversation, { type Turn } from "@/components/rehearsal/Conversation";
import StatePanel from "@/components/rehearsal/StatePanel";
import { clearBannerKinds, MIC_OWNED_KINDS } from "@/voice/bannerPrecedence";
import { handleCapFrame } from "@/voice/costCap";
import { PcmRecorder } from "@/voice/pcmCapture";
import { TtsPlayer } from "@/voice/ttsPlayer";
import type { FallbackBanner, VoiceMode, VoiceStoreSlice } from "@/voice/types";
import { useVoiceFallbacks } from "@/voice/useVoiceFallbacks";
import type { VoiceTurnDeps } from "@/voice/voiceTurn";
import { handleVoiceFrame, sendUtterance } from "@/voice/voiceTurn";

/**
 * Mirrors `agent_session.MAX_CAST`. Two numbers, one fact, so it is
 * asserted against the server's refusal in the tests rather than left to
 * drift: the server is the authority and this only decides when to stop
 * offering the affordance.
 */
const MAX_CAST = 4;

function deriveStageUrl(sessionId: string): string {
  const env = import.meta.env as Record<string, string | undefined>;
  const base = env["VITE_ANIMACHORD_API_BASE"] ?? "/";
  let absolute: string;
  if (base.startsWith("http://") || base.startsWith("https://")) {
    absolute = base;
  } else if (typeof window !== "undefined" && window.location !== undefined) {
    absolute = new URL(base, window.location.origin).toString();
  } else {
    absolute = `http://localhost${base.startsWith("/") ? base : `/${base}`}`;
  }
  const wsBase = absolute.replace(/^http/, "ws").replace(/\/$/, "");
  return `${wsBase}/v1/rehearsal/${sessionId}/stage`;
}

export interface RehearsalProps {
  /**
   * Which surface this route is.
   *
   * `visitor` is a conversation, one field, and the panel — nothing else.
   * `console` is the developer stage: score editor, cue rail, take log.
   * Same socket, same reducer, same frames; only the components that
   * render them differ, which is why #66 is not a rewrite of the data
   * layer.
   */
  readonly surface?: "visitor" | "console";
}

export default function Rehearsal({
  surface = "visitor",
}: RehearsalProps = {}): React.ReactElement {
  const { sessionId: routeSessionId } = useParams<{ sessionId: string }>();
  const [sessionId, setSessionId] = useState<string | null>(routeSessionId ?? null);
  const [connectionState, setConnectionState] = useState<StageSocketState>("connecting");
  const [logLines, setLogLines] = useState<LogLine[]>([]);
  /**
   * What the state panel shows. Folded from frames by a pure reducer
   * rather than assembled here, so the mapping is testable without a
   * socket -- and the mapping is where it can silently go wrong.
   */
  const [panelState, setPanelState] = useState<PanelState>(emptyPanelState);
  /**
   * A character has been spoken to and has not answered yet.
   *
   * Set when the visitor sends, cleared by the first frame that character
   * emits. Without it the page is silent for the seconds a model takes to
   * begin, which is the most likely moment for a stranger to conclude
   * nothing is happening and leave.
   */
  const [awaiting, setAwaiting] = useState<string | null>(null);
  /**
   * The in-flight session creation for this mount attempt.
   *
   * A ref rather than module state because the lifetime has to be the
   * component's: StrictMode's double-invoke is the same instance, so a
   * ref survives it, and a real remount gets a fresh one and therefore a
   * fresh session.
   */
  const sessionCacheRef = useRef<SessionCache["current"]>(
    null,
  ) as SessionCache;

  /**
   * The log, as a conversation.
   *
   * `cue` and any line naming a take id are dropped rather than restyled.
   * A visitor has no use for `take tak_rehearsal_rhs_01M186…` and #66's
   * acceptance forbids it outright; the developer console still shows
   * every line at its own route.
   */
  const visitorTurns: readonly Turn[] = useMemo(() => {
    const out: Turn[] = [];
    const byParent = new Map<string, number>();

    for (const line of logLines) {
      if (line.kind === "cue" || line.text.startsWith("take ")) continue;

      /*
        A blank that arrived marked (#153). Three histories used to paint
        the same nothing here; each now gets what it is owed, and the one
        that is owed nothing gets nothing.

        `treatmentFor` reads the text before the marker, so a reply of
        any length falls straight through to the ordinary path below
        whatever the frame claims. That is the bar this had to clear: a
        signal that fired on "Mm." would land on the demo's most
        characterful turns and would be worse than the blank.
      */
      const treatment = treatmentFor(line.text, line.empty);
      if (treatment === "silent") {
        // Nobody asked. Nothing ran, nothing was spent, and a visitor
        // who sent whitespace does not need telling about it. The take
        // log still records it, because a developer sending a blank
        // note through the SDK does want to know why nothing happened.
        continue;
      }
      if (treatment === "unheard") {
        // The room, unattributed. Naming a character here would make
        // her answer for a microphone that produced nothing.
        out.push({
          id: `unheard:${line.id}`,
          speaker: "room",
          text: conversationCopy.blanks.notHeard,
        });
        continue;
      }

      const speaker =
        line.kind === "agent"
          ? ("character" as const)
          : line.kind === "human"
            ? ("visitor" as const)
            : ("room" as const);

      /*
        A character's reply arrives as several frames -- deltas with
        `partial: true` sharing a `parent_note_id`, then one complete
        frame whose id is that parent. Rendered one-per-frame, a single
        sentence came out as five separate attributed lines:

          MARGO  That
          MARGO  's not a bo
          MARGO  iler problem or a lobby problem, but it's a long
          ...
          MARGO  That's not a boiler problem or a lobby problem, ...

        Found by driving a real turn; no fixture had ever streamed. The
        take log shows every frame and should, because it is a log of
        frames. A conversation is not.

        So deltas accumulate into the turn they belong to and the final
        frame replaces it, which also makes the text appear as it is
        spoken rather than in one jump.
      */
      /*
        Namespaced by speaker, and that is load-bearing rather than tidy.

        `parent_note_id` is **the id of the visitor's note being replied
        to**, and the server reuses that same id as the id of the final
        assistant frame:

          client.human-note  id=01M189YZ…
          server.note        id=msg_de7c…  parent=01M189YZ…  partial
          server.note        id=msg_73b2…  parent=01M189YZ…  partial
          server.note        id=01M189YZ…  parent=01M189YZ…  final

        So keying a character's turn on the parent alone collides with the
        visitor's own line, and the two merge: her reply appeared under
        "You" and what the visitor said vanished.

        A first reading of these frames concluded the parent was the final
        assistant frame's own id. That reading came from a probe printing
        the last six characters of each id, which made the client's ULID
        unrecognisable -- the output was made more readable and stopped
        carrying the thing it was being read for.
      */
      const key =
        speaker === "character"
          ? `character:${line.parentId ?? line.id}`
          : `${speaker}:${line.id}`;
      const at = byParent.get(key);

      if (at === undefined) {
        byParent.set(key, out.length);
        out.push(
          treatment === "no-reply"
            ? {
                id: key,
                speaker,
                name: line.speaker,
                text: conversationCopy.blanks.noReply,
                stageDirection: true,
              }
            : { id: key, speaker, name: line.speaker, text: line.text },
        );
        continue;
      }

      const existing = out[at];
      if (existing === undefined) continue;
      /*
        Text wins a second time, now against the turn rather than the
        frame. The rule below makes the complete frame authoritative,
        which is right for every ordinary turn and would, on a marked
        blank, replace words a visitor has already read with nothing.

        A blank terminal cannot follow partials that carried text -- the
        server assembles the terminal from the same stream it streamed --
        so this branch is not reachable from today's gateway. It is here
        because "cannot happen" is a reason to leave a branch untested,
        not a reason to write the one that erases her line if it does.
      */
      if (treatment === "no-reply") {
        out[at] =
          existing.text.trim().length === 0
            ? {
                ...existing,
                text: conversationCopy.blanks.noReply,
                stageDirection: true,
                partial: false,
              }
            : { ...existing, partial: false };
        continue;
      }
      out[at] = {
        ...existing,
        // The complete frame is authoritative; deltas accumulate.
        text: line.partial === false ? line.text : existing.text + line.text,
        partial: line.partial === true,
      };
    }

    return out;
  }, [logLines]);
  const [cues, setCues] = useState<Cue[]>([]);
  // A reason, not a boolean (#139). The previous `bannerOpen: boolean`
  // rendered one fixed message for every failure, so a visitor who hit the
  // demo's ceiling read "check your connection" and got a retry button that
  // could only fail again.
  const [banner, setBanner] = useState<BannerReason | null>(null);
  const [mountToken, setMountToken] = useState<number>(0);

  // Who is in the room, and who a typed line is addressed to. Both come
  // from the socket rather than from a constant: the resident arrives in
  // `server.hello.voices` and a visitor's character in `server.cast`, so
  // the console never holds a second opinion about the cast.
  const [cast, setCast] = useState<readonly CastMember[]>([]);
  const [addressed, setAddressed] = useState<string>("");

  /**
   * The panel shows whoever the visitor is speaking to.
   *
   * "How **she** knows" is singular, and once the room can hold two people
   * a single list attributes one character's knowledge to the other.
   */

  const [creatingCharacter, setCreatingCharacter] = useState<boolean>(false);
  const [createCharacterError, setCreateCharacterError] = useState<string | null>(null);

  // Voice state (Track H Lane B)
  const [voiceMode, setVoiceMode] = useState<VoiceMode>("text-only");
  const [activeBanner, setActiveBanner] = useState<FallbackBanner | null>(null);

  const socketRef = useRef<StageSocketClient | null>(null);
  // The socket callbacks close over the mount effect, not the latest
  // render, and attribution has to resolve a `voice_id` to a name at the
  // moment a frame lands. Same reason the voice store keeps refs.
  const castRef = useRef<readonly CastMember[]>([]);
  castRef.current = cast;
  const startedAtRef = useRef<number>(Date.now());
  // Latched when the demo runs out of credits. The generic "connection
  // lost — Retry" banner is wrong then: the connection did what it was
  // told, and retrying just re-hits the cap.
  const capReachedRef = useRef<boolean>(false);

  // `VoiceStoreSlice` adapter over local state, so the socket path writes
  // the banner through the same helper the HTTP path uses. The setters
  // from `useState` are stable; the reads go through refs because the
  // socket callbacks close over the mount effect, not the latest render.
  const voiceModeRef = useRef<VoiceMode>(voiceMode);
  const activeBannerRef = useRef<FallbackBanner | null>(activeBanner);
  voiceModeRef.current = voiceMode;
  activeBannerRef.current = activeBanner;
  const voiceStore = useMemo<VoiceStoreSlice>(
    () => ({
      get voiceMode(): VoiceMode {
        return voiceModeRef.current;
      },
      get activeBanner(): FallbackBanner | null {
        return activeBannerRef.current;
      },
      // The refs are written here, synchronously, and not only on the next
      // render. Banner precedence is a read-then-write against current
      // state, and React batches: two frames arriving in one tick would
      // both read the pre-batch value, so the second would displace the
      // first no matter how the ranks compare. That is precisely the case
      // the ranking exists for, since a spent balance and a refused spoken
      // turn arrive back to back on the same socket. The assignments above
      // stay as a repair for any write that bypasses these setters.
      setVoiceMode: (mode: VoiceMode): void => {
        voiceModeRef.current = mode;
        setVoiceMode(mode);
      },
      setActiveBanner: (banner: FallbackBanner | null): void => {
        activeBannerRef.current = banner;
        setActiveBanner(banner);
      },
    }),
    [],
  );

  // Mic acquisition, live permission state, and dead-capture detection.
  const { enableVoice, disableVoice, getStream } = useVoiceFallbacks(voiceStore);

  // Spoken-turn plumbing. The player is one instance for the page so a new
  // reply cuts off the previous one rather than talking over it.
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const playerRef = useRef<TtsPlayer | null>(null);
  const recorderRef = useRef<PcmRecorder | null>(null);

  const score: ScoreDraft = useMemo(
    () => ({
      id: sessionId ?? "ephemeral",
      name: rehearsalStageCopy.defaultScoreName,
      description: rehearsalStageCopy.defaultScoreDescription,
      content: "",
    }),
    [sessionId],
  );

  const appendLog = useCallback((line: LogLine): void => {
    setLogLines((prev) => [...prev, line]);
  }, []);

  /**
   * Resolve a `voice_id` to the name to print in the log margin.
   *
   * Falls back to the id rather than to a blank or to the resident. A
   * blank leaves a line nobody said; the resident's name on someone
   * else's line is a lie the visitor has no way to detect, which is the
   * same failure the socket refuses at the routing layer. An id is ugly
   * and honest, and it only appears if a note arrives for a voice no
   * `server.cast` frame has mentioned.
   */
  const speakerFor = useCallback((voiceId: string): string => {
    const found = castRef.current.find((m) => m.voiceId === voiceId);
    return found?.name ?? voiceId;
  }, []);

  const panelView = useMemo(
    () => viewFor(panelState, addressed || undefined, speakerFor),
    // `cast` is a dependency even though `speakerFor` is stable: it reads
    // `castRef`, so a character arriving without a panel frame would
    // otherwise leave `withheldElsewhere` rendering a voice id.
    //
    // The rule cannot see through the ref. It is correct that `cast` is
    // never *referenced* in the callback and wrong that it is therefore
    // unnecessary: it is the recompute trigger for a ref whose contents it
    // mirrors. Taking the rule's advice reinstates the defect the line
    // above describes, which is why this is disabled rather than obeyed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [panelState, addressed, speakerFor, cast],
  );

  /**
   * Adopt a cast from the socket, keeping the addressed voice sensible.
   *
   * The addressed voice is set once, to whoever is first in the room, and
   * then left alone. Re-pointing it at each new character would move the
   * visitor's next line to someone they did not choose, in the moment
   * they are least expecting it: the frame that adds a character arrives
   * while they are reading, not while they are typing.
   */
  const applyCast = useCallback(
    (voices: readonly { readonly voice_id: string; readonly name: string }[]): void => {
      const next = voices.map((v) => ({ voiceId: v.voice_id, name: v.name }));
      castRef.current = next;
      setCast(next);
      setAddressed((current) => {
        if (current !== "" && next.some((m) => m.voiceId === current)) return current;
        return next[0]?.voiceId ?? "";
      });
    },
    [],
  );

  const handleCreateCharacter = useCallback(
    (sentence: string): void => {
      const socket = socketRef.current;
      if (socket === null) return;
      setCreateCharacterError(null);
      setCreatingCharacter(true);
      socket.send({
        id: newUlid(),
        ts: new Date().toISOString(),
        kind: "client.create-character",
        sentence,
      });
    },
    [],
  );

  // Indirection so `onRetry` can point at the talk handler defined below
  // without the two `useCallback`s having to reference each other.
  const talkRef = useRef<() => void>(() => undefined);

  const buildVoiceDeps = useCallback((): VoiceTurnDeps => {
    playerRef.current ??= new TtsPlayer();
    return {
      socket: socketRef.current,
      store: voiceStore,
      player: playerRef.current,
      // The recognizer's reading of the visitor, in the log lane that
      // already means "the human did this". It is deliberately not an
      // `agent` line: a mistranscription the visitor cannot tell from a
      // character's words is the one thing this frame exists to prevent.
      onTranscript: (text, id, ts): void => {
        /*
          A recognizer that resolved nothing still sends this frame, and
          rendering it produced a row reading `“”` -- a pair of quotation
          marks around nothing, which says less than silence does. The
          terminal note that follows carries `not_heard`, and that is
          where both surfaces say what happened. Saying it twice, once
          badly, is worse than saying it once.
        */
        if (text.trim().length === 0) return;
        appendLog({
          id,
          kind: "cue",
          timestamp: ts,
          text: `“${text}”`,
          // The recognizer's reading of the visitor is still the
          // visitor. Attributing it to a character would make a
          // mistranscription indistinguishable from something a
          // character said, which is the one thing this lane exists
          // to prevent.
          speaker: "you",
        });
      },
      onRetry: (): void => talkRef.current(),
    };
  }, [appendLog, voiceStore]);

  // Mount flow: create session (if missing) + open socket. Re-runs when
  // `mountToken` increments via the Retry button.
  useEffect(() => {
    let cancelled = false;
    setBanner(null);
    setLogLines([]);
    setCues([]);
    setCast([]);
    castRef.current = [];
    setAddressed("");
    setCreatingCharacter(false);
    setCreateCharacterError(null);
    setConnectionState("connecting");
    startedAtRef.current = Date.now();
    capReachedRef.current = false;

    async function start(): Promise<void> {
      let id: string;
      let token: string;
      try {
        const resp = await createSession(sessionCacheRef, mountToken);
        id = resp.session_id;
        token = resp.token;
      } catch (error: unknown) {
        // The error was discarded here, so a 429 ceiling, a 500 and a dead
        // network all rendered "Stage unreachable — check your connection".
        // `ApiError` has carried the status and the server's error code the
        // whole time.
        if (!cancelled) setBanner(bannerReasonFor(error));
        return;
      }
      if (cancelled) return;
      setSessionId(id);

      const socket = new StageSocketClient(deriveStageUrl(id), token, {
        onStateChange: (next): void => {
          if (cancelled) return;
          setConnectionState(next);
          if (next === "closed" && !capReachedRef.current) setBanner("unreachable");
        },
        onClose: (event): void => {
          if (cancelled || capReachedRef.current) return;
          // Surface application-level (4xxx) and abnormal (1006) closes
          // immediately as a banner so the user can choose to retry.
          if (!event.clean || event.code >= 4000) setBanner("unreachable");
        },
        onMessage: (msg: StageMessage): void => {
          if (cancelled) return;
          // Spend exhaustion arrives as a frame, not a status line. Map
          // it to the same banner state the HTTP 429 path produces.
          if (handleCapFrame(msg, voiceStore) !== null) {
            capReachedRef.current = true;
            setBanner(null);
            return;
          }
          // Voice concerns second, so an exhausted balance is never
          // reported as an audio problem. Returns true only for frames
          // that are wholly the voice layer's (transcripts, audio errors);
          // a `server.note` carrying audio still falls through to the log.
          if (handleVoiceFrame(msg, buildVoiceDeps())) return;
          // Fold into the panel before the log branches. The reducer
          // returns the same object for frames that are not its own, so
          // this is free for the ones that are not.
          setPanelState((current) => applyPanelFrame(current, msg, speakerFor));
          // Any word from a character ends the wait, including the first
          // token of a partial stream.
          if (msg.kind === "server.note") setAwaiting(null);
          if (msg.kind === "server.note") {
            appendLog({
              id: msg.id,
              kind: "agent",
              timestamp: msg.ts,
              text: msg.text,
              speaker: speakerFor(msg.voice_id),
              partial: msg.partial,
              parentId: msg.parent_note_id ?? msg.id,
              // Why this frame is blank, when it is (#153). Carried
              // rather than resolved here: the log and the conversation
              // tell a developer and a visitor different true things
              // about the same frame, and each decides its own.
              empty: msg.empty,
            });
          } else if (msg.kind === "server.hello") {
            // The cast arrives with hello, so the rail is populated
            // before the opening line lands and that line has a name to
            // be attributed to.
            applyCast(msg.voices);
            appendLog({
              id: msg.id,
              kind: "system",
              timestamp: msg.ts,
              text: `take ${msg.take_id}`,
            });
          } else if (msg.kind === "server.cast") {
            applyCast(msg.voices);
            setCreatingCharacter(false);
            setCreateCharacterError(null);
          } else if (msg.kind === "server.error") {
            // Character-creation refusals are the composer's to show,
            // not the page banner's: the socket is fine and the
            // visitor's next move is to type a different sentence, so
            // a "connection lost, retry" banner would be both wrong and
            // the loudest thing on screen.
            if (
              msg.error.error === "character_rejected" ||
              msg.error.error === "cast_full"
            ) {
              setCreatingCharacter(false);
              setCreateCharacterError(msg.error.message);
            }
          }
        },
        onError: (): void => {
          if (cancelled || capReachedRef.current) return;
          setBanner("unreachable");
        },
      });
      socketRef.current = socket;
    }

    void start();

    return (): void => {
      cancelled = true;
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [mountToken, appendLog, voiceStore, buildVoiceDeps, applyCast, speakerFor]);

  // Release the audio graph on unmount. The recorder holds an open mic
  // path and the player an AudioContext; neither is React's to collect.
  useEffect(() => {
    return (): void => {
      recorderRef.current?.cancel();
      playerRef.current?.cancel();
    };
  }, []);

  const handleSendCue = useCallback((): void => {
    const socket = socketRef.current;
    if (socket === null) return;
    const cueId = newUlid();
    const at = (Date.now() - startedAtRef.current) / 1000;
    socket.send({
      id: cueId,
      ts: new Date().toISOString(),
      kind: "client.cue",
      dial_id: "human-cue",
      value: at,
    });
    setCues((prev) => [
      ...prev,
      { id: cueId, kind: "client.cue", label: rehearsalStageCopy.sendCueDefaultText, at_seconds: at },
    ]);
    appendLog({
      id: cueId,
      kind: "cue",
      timestamp: new Date().toISOString(),
      text: rehearsalStageCopy.sendCueDefaultText,
    });
  }, [appendLog]);

  /**
   * Send a typed line as `client.human-note`.
   *
   * The visitor's own line goes into the take log immediately rather
   * than waiting for the socket to echo it — the server does not echo
   * client frames, and a reply can take seconds, so without this the
   * log would show the character answering a question nobody appears
   * to have asked.
   */
  const handleSendNote = useCallback(
    (text: string): void => {
      const socket = socketRef.current;
      if (socket === null) return;
      const noteId = newUlid();
      const ts = new Date().toISOString();
      socket.send({
        id: noteId,
        ts,
        kind: "client.human-note",
        text,
        // The routing key, not a label. An unaddressed note used to fall
        // to whoever the server considered default; with two people in
        // the room the visitor has to be able to say which one.
        voice_id: addressed,
      });
      appendLog({ id: noteId, kind: "human", timestamp: ts, text, speaker: "you" });
    },
    [appendLog, addressed],
  );

  const handleRetry = useCallback((): void => {
    setMountToken((v) => v + 1);
  }, []);

  /**
   * Stop capturing and send what was captured.
   *
   * Every outcome other than `sent` is silent on purpose. An empty clip
   * means the visitor tapped twice or their capture path is dead, and the
   * dead case already has its own banner from the silence monitor; a
   * second one saying the same thing in different words is noise. The
   * over-length case cannot happen from this button (the recorder stops
   * itself at the ceiling) and is a guard, not a message.
   */
  const stopAndSend = useCallback((): void => {
    const recorder = recorderRef.current;
    if (recorder === null || !recorder.isRecording) return;
    setIsRecording(false);
    sendUtterance(recorder.stop(), buildVoiceDeps());
  }, [buildVoiceDeps]);

  /**
   * Toggle the microphone for one turn.
   *
   * A toggle rather than push-to-talk: a held button is a worse fit for
   * touch, and a visitor who releases early loses the end of their own
   * sentence with no way to tell that is what happened. The 30-second
   * ceiling in the recorder is what keeps a forgotten toggle from
   * becoming a bill, and it ends the turn by sending rather than
   * discarding, so the words already spoken still get an answer.
   */
  const handleTalkToggle = useCallback((): void => {
    if (recorderRef.current?.isRecording === true) {
      stopAndSend();
      return;
    }
    const stream = getStream();
    if (stream === null) return;
    // A visitor talking over a reply means they want the reply to stop.
    playerRef.current?.cancel();
    recorderRef.current ??= new PcmRecorder({ onCap: () => stopAndSend() });
    if (recorderRef.current.start(stream)) setIsRecording(true);
  }, [getStream, stopAndSend]);
  talkRef.current = handleTalkToggle;

  // Voice toggle. Turning voice on actually asks for the microphone now, and
  // `voiceMode` moves only once the request resolves — the state reflects
  // whether we hold a stream, not whether the button was pressed.
  //
  // Turning it off clears the mic layer's own banners and nothing else: this
  // used to blank the slot unconditionally, which let a shrug at the mic
  // button erase a spent-credits notice the visitor still needed.
  const handleVoiceToggle = useCallback((): void => {
    if (voiceMode === "voice-active") {
      // Drop the half-captured turn rather than sending it: turning the
      // mic off is not a request to be billed for what it heard so far.
      recorderRef.current?.cancel();
      setIsRecording(false);
      disableVoice();
      return;
    }
    clearBannerKinds(voiceStore, MIC_OWNED_KINDS);
    enableVoice();
  }, [voiceMode, voiceStore, enableVoice, disableVoice]);

  return (
    <section data-testid="page-rehearsal" style={{ padding: "1rem 1.25rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "0.5rem" }}>
        {/*
          "Rehearsal" is what a developer calls this route and what a
          visitor has no use for. A stranger arrives to talk to someone;
          a heading naming the tool is the first thing they have to read
          past, and #66's point is that nothing should sit above the
          conversation that they must dismiss.
        */}
        {surface === "console" ? (
          <h1 className="font-sohne-display" style={{ margin: 0, fontSize: "1.25rem" }}>
            Rehearsal
          </h1>
        ) : (
          <span />
        )}
        {/*
          Wraps, because the session id is a thirty-character ULID in a
          monospaced face and does not break. Beside the voice toggle it
          ran 58px past a phone viewport -- the last of the horizontal
          scroll on this page, and the only kind of overflow a visitor
          feels rather than merely sees.
        */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            flexWrap: "wrap",
            minWidth: 0,
          }}
        >
          {voiceMode !== "unsupported" ? (
            <button
              type="button"
              data-testid="rehearsal-voice-toggle"
              aria-label={voiceMode === "voice-active" ? "Turn off voice" : "Turn on voice"}
              aria-pressed={voiceMode === "voice-active"}
              onClick={handleVoiceToggle}
              style={{
                background: voiceMode === "voice-active" ? "var(--brass-cue)" : "transparent",
                color:
                  voiceMode === "voice-active"
                    ? "var(--score-ink)"
                    : "var(--paper-cream-dim)",
                /*
                  Brass, not the wrong-note colour. `--dynamic-forte` is
                  this system's failure red, so a visitor's first second on
                  the page included something that read as an error --
                  about a feature they had not asked for and which is
                  simply off. A control that is available and not currently
                  on is not an alarm.
                */
                border: "1px solid var(--brass-tarnish)",
                borderRadius: "var(--radius)",
                padding: "0.3rem 0.6rem",
                cursor: "pointer",
                fontSize: "0.781rem",
              }}
            >
              {voiceMode === "voice-active" ? "🎙 Voice on" : "🎙 Voice off"}
            </button>
          ) : null}
          {voiceMode === "voice-active" ? (
            <button
              type="button"
              data-testid="rehearsal-talk-toggle"
              aria-label={isRecording ? "Stop and send" : "Start talking"}
              aria-pressed={isRecording}
              onClick={handleTalkToggle}
              style={{
                background: isRecording ? "var(--brass-cue)" : "transparent",
                color: isRecording ? "var(--score-ink)" : "var(--paper-cream)",
                border: "1px solid var(--dynamic-forte)",
                borderRadius: "var(--radius)",
                padding: "0.3rem 0.6rem",
                cursor: "pointer",
                fontSize: "0.781rem",
              }}
            >
              {isRecording ? "◼ Stop & send" : "● Talk"}
            </button>
          ) : null}
          {/*
            The session id is for a developer, not a visitor. #66's
            acceptance forbids session ids on the visitor surface
            outright, and it is the same family as the ISO timestamp the
            take log used to spend 240px on: a debug artifact competing
            for attention with a character.
          */}
          {surface === "console" && (
          <p
            className="text-paper-cream-dim font-plex-mono"
            style={{
              margin: 0,
              fontSize: "0.781rem",
              // A ULID has no spaces, so it needs explicit permission to
              // break or it holds the row open at any viewport.
              overflowWrap: "anywhere",
              minWidth: 0,
            }}
          >
            session: {sessionId ?? rehearsalStageCopy.loading}
          </p>
          )}
        </div>
      </header>

      {/* Socket-error banner (existing — do not remove) */}
      {banner ? (
        <div
          data-testid="rehearsal-banner"
          role="alert"
          className="font-plex-mono"
          style={{
            background: "var(--rehearsal-taupe)",
            border: "1px solid var(--dynamic-forte)",
            borderRadius: "var(--radius)",
            padding: "0.75rem 1rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "0.75rem",
            color: "var(--paper-cream)",
            fontSize: "0.781rem",
          }}
        >
          <div>
            <strong style={{ display: "block" }}>{rehearsalStageCopy.banner[banner].title}</strong>
            <span style={{ color: "var(--paper-cream-dim)" }}>{rehearsalStageCopy.banner[banner].body}</span>
          </div>
          {/* A ceiling does not move because someone pressed a button, and
              offering the button says it might (#139). */}
          {rehearsalStageCopy.banner[banner].retryable ? (
          <button
            type="button"
            data-testid="rehearsal-banner-retry"
            onClick={handleRetry}
            style={{
              background: "var(--brass-cue)",
              color: "var(--score-ink)",
              border: 0,
              borderRadius: "var(--radius)",
              padding: "0.4rem 0.9rem",
              cursor: "pointer",
              fontSize: "0.781rem",
            }}
          >
            {rehearsalStageCopy.banner.retryLabel}
          </button>
          ) : null}
        </div>
      ) : null}

      {/* Voice fallback banner (Track H Lane B — additive) */}
      {activeBanner !== null ? (
        <div
          data-testid="rehearsal-voice-banner"
          role="alert"
          className="font-plex-mono"
          style={{
            background: "var(--rehearsal-taupe)",
            border: "1px solid var(--dynamic-forte)",
            borderRadius: "var(--radius)",
            padding: "0.75rem 1rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "0.75rem",
            color: "var(--paper-cream)",
            fontSize: "0.781rem",
          }}
        >
          <span>{activeBanner.message}</span>
          <div style={{ display: "flex", gap: "0.5rem", flexShrink: 0 }}>
            {activeBanner.retryAction !== undefined ? (
              <button
                type="button"
                onClick={activeBanner.retryAction}
                style={{
                  background: "var(--brass-cue)",
                  color: "var(--score-ink)",
                  border: 0,
                  borderRadius: "var(--radius)",
                  padding: "0.4rem 0.9rem",
                  cursor: "pointer",
                  fontSize: "0.781rem",
                }}
              >
                {activeBanner.retryLabel ?? "Retry"}
              </button>
            ) : null}
            {activeBanner.ctaAction !== undefined ? (
              <button
                type="button"
                onClick={activeBanner.ctaAction}
                style={{
                  background: "var(--brass-cue)",
                  color: "var(--score-ink)",
                  border: 0,
                  borderRadius: "var(--radius)",
                  padding: "0.4rem 0.9rem",
                  cursor: "pointer",
                  fontSize: "0.781rem",
                }}
              >
                {activeBanner.ctaLabel ?? "OK"}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {surface === "visitor" ? (
        <div
          data-testid="visitor-surface"
          /*
            Columns live in the stylesheet, not here. An inline
            `gridTemplateColumns` beats the media query, so the rail never
            engaged and the panel stacked at every width -- a rule that was
            correct and unreachable, which looks exactly like a rule that
            is wrong.
          */
          className="visitor-grid"
          style={{ display: "grid", gap: "1rem", alignItems: "start" }}
        >
          <Conversation
            turns={visitorTurns}
            status={
              connectionState === "open"
                ? "live"
                : connectionState === "closed"
                  ? "lost"
                  : "connecting"
            }
            onSend={(text) => {
              setAwaiting(speakerFor(addressed));
              handleSendNote(text);
            }}
            thinking={awaiting !== null}
            thinkingName={awaiting ?? undefined}
            disabled={connectionState !== "open"}
            /*
              The same cast state the console rail uses. Beat 1 and beat 3
              were unreachable for a visitor only because these four props
              had nowhere to go on this surface, not because anything was
              missing underneath.
            */
            room={cast.map((m) => ({ voiceId: m.voiceId, name: m.name }))}
            addressed={addressed}
            onAddress={setAddressed}
            onBringSomeoneIn={handleCreateCharacter}
            bringingSomeoneIn={creatingCharacter}
            bringError={createCharacterError}
            roomFull={cast.length >= MAX_CAST}
          />
          <StatePanel
            trust={panelState.trust}
            memory={panelView.memory}
            paths={panelView.paths}
            withheld={panelView.withheld}
            olderPaths={panelView.olderPaths}
            olderWithheld={panelView.olderWithheld}
            withheldElsewhere={panelView.withheldElsewhere}
            status={
              connectionState === "open"
                ? "live"
                : connectionState === "closed"
                  ? "failed"
                  : "loading"
            }
          />
        </div>
      ) : (
      <RehearsalStage
        panel={
          <StatePanel
            trust={panelState.trust}
            memory={panelView.memory}
            paths={panelView.paths}
            withheld={panelView.withheld}
            olderPaths={panelView.olderPaths}
            olderWithheld={panelView.olderWithheld}
            withheldElsewhere={panelView.withheldElsewhere}
            status={
              connectionState === "open"
                ? "live"
                : connectionState === "closed"
                  ? "failed"
                  : "loading"
            }
          />
        }
        score={score}
        cues={cues}
        logLines={logLines}
        connectionState={connectionState}
        onSendCue={handleSendCue}
        onSendNote={handleSendNote}
        cast={cast}
        addressed={addressed}
        onAddress={setAddressed}
        onCreateCharacter={handleCreateCharacter}
        creatingCharacter={creatingCharacter}
        createCharacterError={createCharacterError}
        castFull={cast.length >= MAX_CAST}
      />
      )}
    </section>
  );
}
